import { FC, useEffect, useRef, useState } from 'react';
import { rgba } from 'polished';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Container, Row, Col } from 'reactstrap';
import { clx } from 'Utils';
import {
	Colors,
	Address,
	CommunityPartnerProfile,
	CommunityPartnerMe
} from 'Types';
import { useSearchJobs, useModal, useResponsive } from 'Hooks';
import {
	ListJobCard,
	ListJobCardLoading,
	NoData,
	JobFilters,
	ModalBoxSearchFilter,
	CommunityPartnerPreviewJobSearchBox,
	Button,
	FontAwesome
} from 'Elements';
import styles from 'partials/homepage/section/agent-partner-job-section.module.scss';
import { useLocation, useNavigate } from 'react-router-dom';
import { Pagination } from './job/Pagination';

interface Props {
	communityPartnerProfile: CommunityPartnerProfile;
	communityPartnerMe: CommunityPartnerMe;
	address: Address;
	isGlobal?: boolean;
	agentId: string;
}

const OverLay = styled.div`
	${({ theme }) =>
		theme.communityPartner.primaryColor &&
		`background-color: ${rgba(theme.communityPartner.primaryColor, 0.5)}`}
`;

export const CommunityPartnerPreviewJobSearchSection: FC<Props> = ({
	communityPartnerProfile,
	communityPartnerMe,
	address,
	isGlobal,
	agentId
}) => {
	const global = isGlobal;
	const location = useLocation();
	const navigate = useNavigate();
	const query = new URLSearchParams(location.search);
	const bgImage = '/assets/img/job-bg.jpg';
	const refContainer = useRef<HTMLDivElement>(null);
	const { t } = useTranslation();
	const [modal, toggleModal] = useModal();
	const [pageNumber, setPageNumber] = useState(1);
	const { isTabletAndBelow, isDesktopAndAbove, isDesktopAndBelow } =
		useResponsive();

	const availableFilters = [
		query.get('JobTags'),
		query.get('q'),
		query.get('JobActivities'),
		query.get('Locations'),
		query.get('lon'),
		query.get('lat'),
		query.get('distance'),
		query.get('postalCode'),
		query.get('BusinessSectors')
	];
	const hasFilter = availableFilters.some((filter) => Boolean(filter));

	const [filterPostalCode, setFilterPostalCode] = useState({
		lon: global
			? query.get('lon')
				? parseFloat(query.get('lon') as string)
				: address?.location?.longitude ?? ''
			: address?.location?.longitude,
		lat: global
			? query.get('lat')
				? parseFloat(query.get('lat') as string)
				: address?.location?.latitude ?? ''
			: address?.location?.latitude,
		postalCode: global
			? query.get('postalCode')
				? query.get('postalCode')?.toString()
				: address?.postalCode
			: address?.postalCode
	});

	useEffect(() => {
		if (!communityPartnerMe?.commissionGroupInfo?.isPremium) {
			setFilterPostalCode(() => ({
				postalCode: global
					? query.get('postalCode')
						? query.get('postalCode')?.toString()
						: address?.postalCode
					: '',
				lon: global
					? query.get('lon')
						? parseFloat(query.get('lon') as string)
						: address?.location?.longitude ?? ''
					: address?.location?.longitude,
				lat: global
					? query.get('lat')
						? parseFloat(query.get('lat') as string)
						: address?.location?.latitude ?? ''
					: address?.location?.latitude
			}));
		}
	}, [address]);

	const { data: result, isFetching } = useSearchJobs({
		pageNumber: Number(query.get('page')) || 1,
		pageSize: 10,
		keyword: query.get('q')?.toString() ?? '',
		jobTags: query.get('JobTags')?.toString()?.split(',') ?? [],
		jobActivities: query.get('JobActivities')?.toString()?.split(',') ?? [],
		locations: query.get('Locations')?.toString()?.split(',') ?? [],
		kind: null,
		postalCode: filterPostalCode.postalCode as string,
		lon: global
			? filterPostalCode.lon
			: address?.location?.longitude ?? filterPostalCode.lon,
		lat: global
			? filterPostalCode.lat
			: address?.location?.latitude ?? filterPostalCode.lat,
		distance: query.get('distance')
			? parseInt(query.get('distance') as string)
			: 25,
		businessSectors:
			query.get('BusinessSectors')?.toString()?.split(',') ?? [],
		...(communityPartnerMe?.commissionGroupInfo?.isPremium && {
			communityPartnerId: agentId
		}),
		categories: []
	});

	const setSearchParams = (params: Record<string, string>) => {
		const updatedQuery = new URLSearchParams(location.search);
		Object.keys(params).forEach((key) => {
			updatedQuery.set(key, params[key]);
		});
		navigate({ search: updatedQuery.toString() }, { replace: true });
	};

	const clearSearchParams = (keys: string[]) => {
		const updatedQuery = new URLSearchParams(location.search);
		keys.forEach((key) => {
			updatedQuery.delete(key);
		});
		navigate({ search: updatedQuery.toString() }, { replace: true });
	};

	const handlePageChange = (page: number) => {
		setPageNumber(page);
		setSearchParams({ page: page.toString() });
	};

	const handleReset = () => {
		clearSearchParams([
			'page',
			'JobTags',
			'Locations',
			'JobActivities',
			'lon',
			'lat',
			'distance',
			'postalCode',
			'q'
		]);
	};

	const scrollIntoComponent = () => {
		if (refContainer.current) {
			window.scrollTo({
				top: refContainer.current?.offsetTop,
				behavior: 'smooth'
			});
		}
	};

	useEffect(() => {
		if (window.location.hash || pageNumber > 1) {
			scrollIntoComponent();
		}
	}, [pageNumber]);

	useEffect(() => {
		setPageNumber(
			query.get('page') ? parseInt(query.get('page') as string) : 1
		);
	}, [filterPostalCode]);

	return (
		<section className={styles.wrapper} id="agent-job" ref={refContainer}>
			looooooooooo
			<div className={styles.background}>
				<img src={bgImage} alt="jobs-bg" className="w-100" />
				<OverLay className={styles.background_overlay} />
			</div>
			<Container className="position-relative">
				<header className="d-flex align-items-lg-center flex-column flex-lg-row mb-4 mb-lg-0">
					{communityPartnerProfile?.jobSearch?.headline && (
						<h3 className="h4 fw-bold mb-0 pe-lg-3 text-center text-gray-6">
							{communityPartnerProfile?.jobSearch?.headline}
						</h3>
					)}
					<div className="d-flex flex-wrap flex-lg-nowrap align-items-center ms-lg-auto gap-2 mt-5 mb-lg-5">
						<div className="d-flex justify-content-center w-100 d-block d-lg-none">
							<Button
								color={Colors.primary}
								title={t('button.filters')}
								className={clx(
									'p-1 rounded-circle d-block d-lg-none flex-shrink-0',
									styles.filter_btn
								)}
								onClick={toggleModal}>
								<FontAwesome
									icon="filter"
									swapOpacity
									size={isTabletAndBelow ? 'xl' : 'lg'}
								/>
							</Button>
						</div>
						<CommunityPartnerPreviewJobSearchBox
							setFilterPostalCode={setFilterPostalCode}
							filterPostalCode={filterPostalCode as any}
							isGlobal={global}
							className="flex-grow-1 flex-lg-grow-0"
						/>
					</div>
				</header>
				<Row>
					{isDesktopAndAbove && (
						<Col lg={4} xl={3}>
							<JobFilters
								className={styles.filter_box}
								list={result?.facets}
								filters={result?.filter}
								setPageNumber={setPageNumber}
								showDistance={
									!filterPostalCode?.postalCode ||
									filterPostalCode?.postalCode === ''
										? false
										: true
								}
								isLoading={isFetching}
								onReset={hasFilter && handleReset}
							/>
						</Col>
					)}
					<Col lg={8} xl={9} className="d-flex flex-column">
						{isFetching && <ListJobCardLoading count="8" />}
						{!isFetching &&
							result?.results?.map((job) => {
								return (
									<ListJobCard
										key={job.document.jobId}
										job={job.document as any}
									/>
								);
							})}
						{!isFetching && result?.totalCount === 0 && (
							<div className="d-flex h-100 justify-content-center">
								<NoData
									message={t('title.no-jobs-found')}
									boxClassName="col-8 col-lg-5 col-md-6">
									<Button
										className="mt-5 px-lg-5 px-3"
										onClick={handleReset}>
										{t('button.reset-filters')}
									</Button>
								</NoData>
							</div>
						)}
						{!isFetching &&
							result &&
							result.totalCount > 0 &&
							result.totalPages > 1 &&
							result.totalPages === result.currentPage && (
								<div className="text-gray-3 fs-small my-4 py-2 text-center">
									{t('text.end-of-list')}
								</div>
							)}
						{!isFetching && result && result?.totalPages > 1 && (
							<Pagination
								className="mt-3"
								totalItemsCount={result?.totalCount}
								activePage={pageNumber}
								itemsCountPerPage={8}
								onChange={handlePageChange}
							/>
						)}
					</Col>
				</Row>
			</Container>
			{isDesktopAndBelow && (
				<ModalBoxSearchFilter
					isOpen={modal}
					onClose={toggleModal}
					totalItems={result?.totalCount as number}
					isLoading={isFetching}
					onReset={hasFilter && handleReset}
					type="job">
					<JobFilters
						list={result?.facets}
						isLoading={isFetching}
						onReset={hasFilter && handleReset}
						className={styles.box}
					/>
				</ModalBoxSearchFilter>
			)}
		</section>
	);
};
